/* Copyright (c) 2023 MOCA Systems, Inc.  All rights reserved. */

import {
	Component,
	AfterViewInit,
	OnDestroy,
	Output,
	EventEmitter,
	ViewChild,
	ElementRef,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	HostListener,
} from '@angular/core';
import { ResizableContainerComponent } from '../resizable-container/resizable-container.component';
import { ContainerLayerManager } from '../base-container/ContainerLayerManager';
import { HoverOverStateService } from 'src/app/services/hover-over-state.service';
import { combineLatest, Observable, Subject } from 'rxjs';
import { withLatestFrom, map, distinctUntilChanged, takeUntil, debounceTime } from 'rxjs/operators';

@Component({
	selector: 'hover-over-container',
	templateUrl: './hover-over-container.component.html',
	styleUrls: [
		'./hover-over-container.component.scss',
		'../resizable-container/resizable-container.component.scss',
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HoverOverContainerComponent
	extends ResizableContainerComponent
	implements AfterViewInit, OnDestroy
{
	@Output() public close: EventEmitter<void> = new EventEmitter();

	@ViewChild('container', { static: false }) public container: ElementRef;

	private shutdownSubject$: Subject<void> = new Subject();

	constructor(
		protected changeDetectorRef: ChangeDetectorRef,
		protected containerLayerManager: ContainerLayerManager,
		public hoverOverStateService: HoverOverStateService
	) {
		super(changeDetectorRef, containerLayerManager);
	}

	@HostListener('mouseenter') public onMouseEnter() {
		this.hoverOverStateService.startHoverComponent();
	}

	@HostListener('mouseleave') public onMouseLeave() {
		this.hoverOverStateService.endHoverComponent();
	}

	public ngAfterViewInit(): void {
		super.ngAfterViewInit();

		const heightChangeObserver = new Observable((subscriber) => {
			const resizer = new ResizeObserver((entries) => {
				if (entries.length > 1) {
					subscriber.error(
						new Error('There are more than one instance of HoverOverContainerComponent on the page')
					);
				}

				for (const entry of entries) {
					subscriber.next(entry.contentRect.height);
				}
			});
			resizer.observe(document.querySelector('.container'));
			return () => {
				resizer.disconnect();
			};
		});

		combineLatest([
			this.hoverOverStateService.hoverOverTicketId$,
			this.hoverOverStateService.hoverPosition$,
			heightChangeObserver,
		])
			.pipe(
				debounceTime(20),
				withLatestFrom(this.hoverOverStateService.combinedHoverVisibilityObservable$),
				map(([[ticketId, coordinates, height], isHoverEnabled]) => ({
					ticketId,
					coordinates,
					isHoverEnabled,
					height,
				})),
				distinctUntilChanged(
					(initialData, receievedData) =>
						receievedData.isHoverEnabled &&
						initialData.ticketId === receievedData.ticketId &&
						initialData.height === receievedData.height
				),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe(({ ticketId, coordinates }) => {
				if (ticketId) {
					this.updatePosition(coordinates.x, coordinates.y);
				}
			});
	}

	public ngOnDestroy(): void {
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
		super.destroy();
	}
}
